import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration } from '@sentry/browser';
import { environment } from 'src/environments/environment';
import { LogType } from './log.service.types';
import pkg from '../../../../package.json';

type CaptureExceptionParameters = Parameters<typeof Sentry.captureException>;

@Injectable({
  providedIn: 'root',
})
export class LogService {
  public static setUser(user: Keycloak.KeycloakProfile) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username
    });
  }

  public static resetUser() {
    Sentry.setUser(null);
  }

  public static init() {
    if (!environment.sentryDsn) {
      return;
    }

    const envUrls = Object.keys(environment)
      .filter(key => key.endsWith('BaseUrl') && !key.startsWith('keycloak'))
      .map(key => (environment as Record<string, any>)[key]);

    Sentry.init({
      dsn: environment.sentryDsn,
      integrations: [browserTracingIntegration()],
      environment: environment.production ? 'production' : 'development',
      tracesSampleRate: environment.sentryTracesSampleRate,
      tracePropagationTargets: ['localhost', ...envUrls],
      release: pkg.version,
    });
  }

  public captureException(exception: Error, captureContext?: CaptureExceptionParameters[1], logAlways: boolean = false) {
    if (environment.production || logAlways) {
      Sentry.captureException(exception, captureContext);
    }
  }

  public logComponentError(error: Error, logType: LogType, componentName?: string, extra?: Record<string, unknown>): void {
    this.captureException(error, {
      tags: {
        logType,
        ...(componentName ? { component: componentName } : {}),
      },
      extra,
    });
  }

  public createErrorInstance(error: any): Error {
    if (error instanceof Error) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      return new Error(error.message, {
        cause: error.error
      });
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    return new Error('Non-Error exception captured', {
      cause: error
    });
  }
}
